<template>
	<!-- Plain wrapper -->
	<div>
		<!-- Something to click on to trigger the modal -->
		<slot name="trigger"></slot>

		<!-- Send the modal to a far-off element -->
		<portal :selector="selector">
			<transition @after-enter="$emit('enter')" :name="type">
				<div :class="modalClasses" v-if="visible" v-body-scroll-lock="visible">
					<!-- Modal background -->
					<div class="modal-background" v-if="!isBackgroundHidden" @click="$emit('close')"></div>

					<!-- Modal content -->
					<component :is="rootElement" class="modal-content" @submit.prevent>
						<!-- Modal close -->
						<div class="modal-close type--l" v-if="showCloseButton" @click="$emit('close')">
							<img src="@/assets/icons_close-modal.svg" />
						</div>

						<!-- Modal header -->
						<div class="modal-header card-header" v-if="isHeaderVisible">
							<h2 class="type--i type--l">{{title}}</h2>
						</div>

						<!-- Modal body -->
						<div class="modal-body" v-if="isBodyVisible">
							<slot></slot>
						</div>

						<!-- Modal footer -->
						<div class="modal-footer card-footer" v-if="isFooterVisible">
							<div class="form-group is-compact">
								<button
									v-if="submit"
									@click="$emit('submit')"
									class="button is-primary is-wide type--serif type--m"
									type="submit"
								>{{ submit }}</button>
							</div>
							<div class="modal-footer__secondary">
								<a
									v-if="!!secondary && !!secondary.text"
									role="button"
									@click="$emit('secondary-action', secondary.payload)"
									class="link type--centred type--sans type--s"
								>{{ secondary.text }}</a>
							</div>
						</div>
					</component>
				</div>
			</transition>
		</portal>
	</div>
</template>

<script>
export default {
	props: {
		// Modal content element wrapper
		rootElement: {
			type: String,
			default: "div",
		},
		// Vue portal element css selector
		selector: {
			type: String,
			default: "#m-input",
		},
		// Type of modal transition
		type: {
			type: String,
			default: "fade",
		},
		// Modal header title
		title: String,
		// Submit button text
		submit: {
			type: String,
			default: "",
		},
		// Secondary footer content
		secondary: {
			text: {
				type: String,
				default: "",
			},
			payload: {
				default: null,
			},
		},
		// Include transparent modal background
		showBackground: {
			type: Boolean,
			default: true,
		},
		// Include modal close button
		showCloseButton: {
			type: Boolean,
			default: true,
		},
		// Modal visibility
		visible: {
			type: Boolean,
			default: false,
		},
	},

	computed: {
		modalClasses() {
			return this.isFullscreen ? "modal is-fullscreen" : "modal"
		},
		isHeaderVisible() {
			return !!this.title
		},
		isBodyVisible() {
			return !!this.$slots.default
		},
		isFooterVisible() {
			return this.submit || this.secondary
		},
		isDrawerType() {
			return this.type.startsWith("drawer")
		},
		isFullscreen() {
			return this.isDrawerType
		},
		isBackgroundHidden() {
			return this.isDrawerType || !this.showBackground
		}
	},
}
</script>

<style lang="scss" scoped>
.modal {
	bottom: 0;
	display: flex;
	height: 100vh;
	height: calc(100 * var(--vh, 1vh));
	left: 0;
	padding: 2rem;
	position: absolute;
	transform: translate3d(0, 0, 0);
	width: 100vw;
	&.is-fullscreen {
		padding: 0;
	}
}

.modal-background {
	background-color: rgba(0, 0, 0, 0.5);
	bottom: -1px;
	left: -1px;
	position: absolute;
	right: -1px;
	top: -1px;
	z-index: 0;
}

.modal-content {
	background-color: #fff;
	border-radius: 0.25rem;
	box-shadow: $shadow-medium;
	display: flex;
	flex-direction: column;
	margin: auto;
	max-height: 100%;
	max-width: 24rem;
	overflow: hidden;
	position: relative;
	width: 100%;
	z-index: 1;
	.is-fullscreen & {
		border-radius: 0;
		margin: 0;
		max-width: 100%;
	}
}

.modal-header,
.modal-body,
.modal-footer {
	padding: 1rem;
}

.modal-header {
	align-items: center;
	display: flex;
	flex-direction: row;
	& * {
		line-height: 1.1;
	}
}

.modal-body {
	flex: 1;
	overflow-y: auto;
}

.modal-footer__secondary {
	margin-top: 0.5rem;
	text-align: center;
}

.modal-close {
	border-radius: 50%;
	cursor: pointer;
	height: 1.5rem;
	position: absolute;
	right: 2.125rem;
	top: 2.125rem;
	transform: translate(50%, -50%);
	width: 1.5rem;
	z-index: 2;
	& * {
		display: block;
		width: 100%;
	}
}

/*------------------------------------------------------
 Transitions
 ------------------------------------------------------*/

.drawerUp-enter,
.drawerUp-leave-to {
	transform: translateY(100%);
}
.drawerUp-enter-to,
.drawerUp-leave {
	transform: translateY(0);
}
.drawerUp-enter-active,
.drawerUp-leave-active {
	transition: transform 450ms;
}
</style>